import type { RequiredCommonQueryParams } from '~/@types/backend-types'
import type { ServiceDict } from '~/API/externals'
import { responseInterceptor } from '~/API/toolbox'
import { getCurrentLangDefinition, getDeviceProps, isLocalhost } from '~/config/constants'
import { getQueryClientSession } from '~/query'

import type { AxiosRequestOptions } from '@capture/client-api/src/orval/customInstance'

export type HostType = keyof ServiceDict

/**
 * Build authorization header for API requests.
 *
 * @param authToken - Authentication token (undefined for public/unauthenticated access)
 * @param password - Optional job password for foreign auth
 * @returns Authorization header object, or empty object if no authToken
 */
export const getAuthHeader = (
    authToken: string | undefined,
    password?: string,
): { Authorization: string } | Record<string, never> => {
    if (authToken === undefined) {
        return {}
    }

    if (password) {
        return {
            Authorization: `Basic ${btoa(`FOREIGN+PUBLIC:${authToken} ${password}`)}`,
        }
    }

    return {
        Authorization: `Basic ${btoa(`USER:${authToken}`)}`,
    }
}

/**
 * Build common query params for API requests
 * Includes: API key, client version, device ID, app language
 * Optional: foreign-auth for accessing shared resources
 */
export type CommonQueryParamsWithForeignAuth = RequiredCommonQueryParams & {
    /**
     * Foreign auth
     * ---------------
     * Foreign auth is used when we fetch an job that doesnt belong to the logged in user
     * In those cases, the Auth token becomes foreign-auth
     * If the job is password protected, the job password is sent in place of the auth token
     *
     * @example Accessing a job with user auth:
     * Authorization: `Basic ${btoa(`USER:${authToken}`)}`
     *
     * @example Accessing a password protected job with foreign auth:
     * Authorization: `Basic ${btoa(`FOREIGN+PUBLIC:${authToken} ${password}`)}`
     */
    'foreign-auth'?: string
}

/**
 * Build common query params for API requests.
 *
 * @param extra - Optional extra params to include (e.g., foreign-auth)
 */
export function buildCommonParams(
    extra?: Record<string, string>,
): CommonQueryParamsWithForeignAuth {
    return {
        key: import.meta.env.VITE_API_KEY,
        client_v: import.meta.env.VITE_VERSION,
        device_id: getDeviceProps().device_id,
        app_lang: getCurrentLangDefinition().appLang,
        ...extra,
    }
}

/**
 * Get foreign-auth query parameter from a password.
 *
 * @param password - The password to use for foreign auth (or undefined)
 * @returns Object with foreign-auth param, or undefined if no password provided
 */
export function getForeignAuthParam(
    password: string | undefined,
): { 'foreign-auth': string } | undefined {
    return password ? { 'foreign-auth': password } : undefined
}

/**
 * Resolve hostname for a given host type
 */
function resolveHostname(hostType: HostType, serviceDict?: ServiceDict): string {
    if (serviceDict) {
        return serviceDict[hostType]
    }
    // Fallback for when serviceDict is not available
    return isLocalhost ? 'a0-client.univex.no' : 'a10.cptr.no'
}

/**
 * Get base path for a given host type
 */
function getBasePath(hostType: HostType): string {
    return hostType === 'thumbHost' ? '/th' : '/st/4'
}

/**
 * Options for building request options.
 */
export type BuildRequestOptionsParams = {
    /** Type of host (defaults to appHost) */
    hostType?: HostType
    /** Optional auth token override (uses session token if not provided) */
    authToken?: string
    /** Optional serviceDict override (uses session serviceDict if not provided) */
    serviceDict?: ServiceDict
    /** Optional password for foreign auth (password-protected jobs) */
    password?: string
}

/**
 * Build axios request options for API requests.
 *
 * Uses session-based authToken and serviceDict as defaults.
 * Falls back to default hosts if session is not initialized.
 *
 * If password is provided, includes it in the auth header for foreign auth.
 * If no authToken is available (neither from params nor session), returns empty auth header.
 *
 * @example Basic usage (uses session defaults)
 * buildRequestOptions()
 *
 * @example With specific host
 * buildRequestOptions({ hostType: 'thumbHost' })
 *
 * @example With password for password-protected jobs
 * buildRequestOptions({ hostType: 'appHost', password: getPasswordForJob(jobID) })
 *
 * @example Override session values (for services with explicit auth)
 * buildRequestOptions({ authToken: customToken, serviceDict: customServices })
 */
export function buildRequestOptions({
    hostType = 'appHost',
    authToken,
    serviceDict,
    password,
}: BuildRequestOptionsParams = {}): AxiosRequestOptions {
    const session = getQueryClientSession()
    const token = authToken ?? session?.authToken
    const services = serviceDict ?? session?.serviceDict

    const basePath = getBasePath(hostType)
    const hostname = resolveHostname(hostType, services)

    return {
        baseURL: `https://${hostname}${basePath}`,
        headers: getAuthHeader(token, password),
        responseInterceptors: [responseInterceptor],
    }
}
